import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProductWithWarehouseInventory } from '../../services/productService';
import InventoryTabs from '../inventory/InventoryTabs';
import ProductInventoryHistory from './ProductInventoryHistory';
import './ProductDetails.css';

interface ProductDetailsProps {
  useMockData?: boolean;
}

const ProductDetails: React.FC<ProductDetailsProps> = ({ useMockData = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [product, setProduct] = useState<any>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);

  useEffect(() => {
      const fetchProductData = async () => {
        if (!id) return;
  
        try {
          setLoading(true);
          setError(null);
          console.log(`Fetching product data for ID: ${id}`);
          const productData = await getProductWithWarehouseInventory(id);
          console.log('Product data fetched successfully:', productData ? 'Found' : 'Not found');
          setProduct(productData);
        } catch (err: any) {
          console.error('Error fetching product details:', err);
          
          // Provide more specific error messages based on error type
          if (err.name === 'NetworkError' || !navigator.onLine) {
            setError('فشل في الاتصال بالخادم. يرجى التحقق من اتصالك بالإنترنت والمحاولة مرة أخرى.');
          } else if (err.response && err.response.status === 404) {
            setError('المنتج غير موجود أو تم حذفه.');
          } else if (err.response && err.response.status === 401) {
            setError('غير مصرح لك بالوصول إلى هذا المنتج. يرجى تسجيل الدخول مرة أخرى.');
          } else {
            setError('فشل في تحميل بيانات المنتج. الرجاء المحاولة مرة أخرى لاحقاً.');
          }
        } finally {
          setLoading(false);
        }
      };
  
      fetchProductData();
    }, [id]);
    
    // Set the first variant as selected by default when product data loads
    useEffect(() => {
      if (product?.variants && product.variants.length > 0 && !selectedVariantId) {
        setSelectedVariantId(product.variants[0]._id);
      }
    }, [product, selectedVariantId]);
    
    // Function to handle inventory transaction completion
    const handleTransactionComplete = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const productData = await getProductWithWarehouseInventory(id);
        setProduct(productData);
        setError(null);
      } catch (err: any) {
        console.error('Error refreshing product data:', err);
      } finally {
        setLoading(false);
      }
    };

  const handleBack = () => {
    navigate(-1);
  };

  const handleEdit = () => {
    if (id) {
      navigate(`/products/edit/${id}`);
    }
  };

  const handleDelete = () => {
    // Implement delete functionality
    if (window.confirm('هل أنت متأكد من حذف هذا المنتج؟')) {
      // Delete product
    }
  };

  if (loading) {
    return <div className="loading-container">جاري تحميل بيانات المنتج...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-icon">⚠️</div>
        <h3>حدث خطأ</h3>
        <p>{error}</p>
        <div className="error-actions">
          <button
            className="button secondary-button"
            onClick={handleBack}
          >
            العودة
          </button>
          <button
            className="button primary-button"
            onClick={() => {
              setLoading(true);
              setError(null);
              const fetchProductData = async () => {
                try {
                  const productData = await getProductWithWarehouseInventory(id || '');
                  setProduct(productData);
                  setError(null);
                } catch (err) {
                  console.error('Error retrying product fetch:', err);
                  setError('فشل في تحميل بيانات المنتج. الرجاء المحاولة مرة أخرى لاحقاً.');
                } finally {
                  setLoading(false);
                }
              };
              fetchProductData();
            }}
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  if (!product) {
    return <div className="not-found-container">المنتج غير موجود</div>;
  }

  // Format date for display
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    };
    return new Date(dateString).toLocaleDateString('ar-EG', options);
  };



  return (
    <main className="content-area">
      <div className="content-header">
        <h2 className="content-title">تفاصيل المنتج</h2>
        <div className="action-buttons">
          <button className="button secondary-button" onClick={handleBack}>
            <span>↩️</span>
            <span>العودة</span>
          </button>
          <button className="button primary-button" onClick={handleEdit}>
            <span>✏️</span>
            <span>تعديل</span>
          </button>
          <button className="button danger-button" onClick={handleDelete}>
            <span>🗑️</span>
            <span>حذف</span>
          </button>
        </div>
      </div>

      {/* Basic Info Section */}
      <div className="product-section">
        <div className="product-info-grid">
          <div>
            <h3 className="product-name">{product.name}</h3>
            <p className="product-description">{product.description}</p>
          </div>
          <div>
            <ul className="product-details-list">
              <li>
                <span className="detail-label">الحالة:</span>
                <span className={`status-chip status-${product.status}`}>
                  {product.status === 'active' ? 'نشط' : 
                   product.status === 'inactive' ? 'غير نشط' : 'متوقف'}
                </span>
              </li>
              <li>
                <span className="detail-label">عدد المتغيرات:</span>
                <span className="detail-value">
                  {product.variants?.length || 0}
                </span>
              </li>
              <li>
                <span className="detail-label">تاريخ الإنشاء:</span>
                <span className="detail-value">
                  {product.createdAt ? formatDate(product.createdAt) : 'غير متوفر'}
                </span>
              </li>
              <li>
                <span className="detail-label">آخر تحديث:</span>
                <span className="detail-value">
                  {product.updatedAt ? formatDate(product.updatedAt) : 'غير متوفر'}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>


      {/* Attributes Section */}
      {product.attributeNames && product.attributeNames.length > 0 && (
        <div className="product-section">
          <h3 className="section-title">خصائص المنتج</h3>
          <div className="attribute-cards">
            {product.attributeNames.map((attribute: any, index: number) => (
              <div className="attribute-card" key={index}>
                <div className="attribute-name">{attribute.name}</div>
                <div className="attribute-values">
                  {attribute.values.map((value: string, valueIndex: number) => (
                    <span className="attribute-value" key={valueIndex}>
                      {value}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Variants Section */}
      {product.variants && product.variants.length > 0 && (
        <div className="product-section">
          <h3 className="section-title">متغيرات المنتج</h3>
          <table className="variants-table">
            <thead>
              <tr>
                <th>المتغير</th>
                <th>الوحدة</th>
                <th>الكمية الإجمالية</th>
                <th>المستودعات</th>
                <th>السعر</th>
                <th>رمز المنتج (SKU)</th>
                <th>الباركود</th>
                <th>الحالة</th>
                <th>إدارة المخزون</th>
              </tr>
            </thead>
            <tbody>
              {product.variants.map((variant: any, index: number) => {
                // Format attributes for display
                const attributesText = Object.entries(variant.attributes || {})
                  .map(([key, value]) => `${key}: ${value}`)
                  .join('، ');

                // Get default barcode and its price
                const defaultBarcode = variant.barcodes?.find((b: any) => b.isDefault);
                const defaultPrice = defaultBarcode?.price || '';
                const defaultBarcodeCode = defaultBarcode?.code || '';
                
                // Map other barcodes to price format
                const otherPrices = variant.barcodes?.filter((b: any) => !b.isDefault).map((b: any) => ({
                  name: b.unit || 'سعر إضافي',
                  value: b.price || 0
                })) || [];

                // Check if this variant is selected
                const isSelected = selectedVariantId === variant._id;

                return (
                  <tr key={index} className={isSelected ? 'selected-variant' : ''}>
                    <td>متغير {variant.serialNumber || index + 1}: {attributesText || 'النسخة الافتراضية'}</td>
                    <td>{variant.unit || 'قطعة'}</td>
                    <td>{variant.quantity || 0}</td>
                    <td>
                      {variant.warehouseInventory && variant.warehouseInventory.length > 0 ? (
                        <div className="warehouse-inventory-container">
                          <table className="warehouse-table">
                            <thead>
                              <tr>
                                <th>المستودع</th>
                                <th>الكمية الحالية</th>
                                <th>الكمية المحجوزة</th>
                                <th>الحد الأدنى</th>
                              </tr>
                            </thead>
                            <tbody>
                              {variant.warehouseInventory
                                .sort((a: any, b: any) => a.warehouseName.localeCompare(b.warehouseName))
                                .map((warehouse: any, warehouseIndex: number) => {
                                  // Determine if this warehouse has low stock
                                  const hasStock = warehouse.availableQuantity > 0;
                                  const hasLowStock = hasStock && warehouse.availableQuantity < (warehouse.minimumQuantity || 0);
                                  
                                  return (
                                    <tr key={warehouseIndex}
                                        className={!hasStock ? 'no-stock' : hasLowStock ? 'low-stock' : 'has-stock'}>
                                      <td className="warehouse-name">
                                        {warehouse.warehouseName || 'غير معروف'}
                                        {warehouse.warehouseLocation && <span className="warehouse-location"> ({warehouse.warehouseLocation})</span>}
                                      </td>
                                      <td className="warehouse-quantity">
                                        <span className="quantity-badge">{warehouse.availableQuantity || 0}</span>
                                      </td>
                                      <td className="warehouse-reserved">
                                        {warehouse.reservedQuantity > 0 &&
                                          <span className="reserved-badge">{warehouse.reservedQuantity}</span>
                                        }
                                        {warehouse.reservedQuantity === 0 && '0'}
                                      </td>
                                      <td className="warehouse-min-quantity">
                                        {warehouse.minimumQuantity || 0}
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <span className="no-data">لا توجد بيانات مستودعات</span>
                      )}
                    </td>
                    <td>
                      <ul className="price-list">
                        {defaultPrice && (
                          <li className="default-price">
                            <span>سعر التجزئة:</span>
                            <span>{defaultPrice}</span>
                          </li>
                        )}
                        {otherPrices.map((price: any, priceIndex: number) => (
                          <li key={priceIndex}>
                            <span>{price.name}:</span>
                            <span>{price.value}</span>
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td>{variant.sku || defaultBarcode?.sku || '-'}</td>
                    <td>{defaultBarcodeCode}</td>
                    <td>
                      <span className={`status-chip status-${variant.status}`}>
                        {variant.status === 'active' ? 'نشط' :
                         variant.status === 'inactive' ? 'غير نشط' :
                         variant.status === 'outOfStock' ? 'نفذت الكمية' : variant.status}
                      </span>
                    </td>
                    <td>
                      <button
                        className={`button ${isSelected ? 'primary-button' : 'secondary-button'}`}
                        onClick={() => setSelectedVariantId(variant._id)}
                      >
                        {isSelected ? 'مُحدد' : 'تحديد'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {/* Inventory Management Section for Selected Variant */}
          {selectedVariantId && (
            <div className="inventory-management-section">
              {product.variants.filter(v => v._id === selectedVariantId).map(selectedVariant => {
                // Format attributes for display
                const attributesText = Object.entries(selectedVariant.attributes || {})
                  .map(([key, value]) => `${key}: ${value}`)
                  .join('، ');
                
                // Get SKU from variant or default barcode
                const defaultBarcode = selectedVariant.barcodes?.find(b => b.isDefault);
                const skuDisplay = selectedVariant.sku || defaultBarcode?.sku || '-';
                
                // Get total quantity from all warehouses
                const totalQuantity = selectedVariant.warehouseInventory ?
                  selectedVariant.warehouseInventory.reduce((sum, inv) => sum + (inv.availableQuantity || 0), 0) :
                  selectedVariant.quantity || 0;

                // Get warehouses with stock
                const warehousesWithStock = selectedVariant.warehouseInventory ?
                  selectedVariant.warehouseInventory.filter(inv => inv.availableQuantity > 0).length : 0;

                // Get total warehouses
                const totalWarehouses = selectedVariant.warehouseInventory ?
                  selectedVariant.warehouseInventory.length : 0;
                
                return (
                  <div key={selectedVariant._id} className="inventory-management-container">
                    <div className="inventory-management-header">
                      <h3>إدارة مخزون المتغير: {attributesText || 'النسخة الافتراضية'} {skuDisplay !== '-' ? `(SKU: ${skuDisplay})` : ''}</h3>
                      <div className="inventory-summary-card">
                        <div className="inventory-summary-item">
                          <span className="summary-label">إجمالي المخزون:</span>
                          <span className="summary-value">{totalQuantity} {selectedVariant.unit || 'قطعة'}</span>
                        </div>
                        <div className="inventory-summary-item">
                          <span className="summary-label">المستودعات:</span>
                          <span className="summary-value">{warehousesWithStock} من {totalWarehouses}</span>
                        </div>
                        <div className="inventory-summary-item">
                          <span className="summary-label">حالة المخزون:</span>
                          <span className={`status-chip status-${totalQuantity > 0 ? 'active' : 'outOfStock'}`}>
                            {totalQuantity > 0 ? 'متوفر' : 'غير متوفر'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Inventory Tabs */}
                    <InventoryTabs
                      productId={product._id}
                      productName={product.name}
                      variantId={selectedVariant._id}
                      variantAttributes={selectedVariant.attributes}
                      unitId={selectedVariant.unit ? selectedVariant.unit : ''}
                      unitName={selectedVariant.unit || 'قطعة'}
                      onTransactionComplete={handleTransactionComplete}
                    />
                    
                    {/* Inventory History */}
                    <ProductInventoryHistory
                      productId={product._id}
                      variantId={selectedVariant._id}
                      title={`سجل حركة المخزون - ${attributesText || 'النسخة الافتراضية'}`}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </main>
  );
};

export default ProductDetails;